<template>
  <!-- Карточка слайдера выгод: подарок за частоту заказов. -->
  <div
    v-if="shouldShow"
    class="weekly-gift"
    :style="{ '--benefit-tone': BENEFIT_TONES.weeklyGift }"
    data-testid="weekly-gift"
  >
    <BasketGoalRing :percent="progressPercent" :tone="BENEFIT_TONES.weeklyGift">
      🏆
    </BasketGoalRing>

    <div class="weekly-gift__content">
      <!-- Условие выполнено: предлагаем забрать подарок -->
      <template v-if="progress.available">
        <div class="weekly-gift__title">
          <span class="weekly-gift__accent">{{ giftProduct?.name }}</span> — в подарок!
        </div>
        <div class="weekly-gift__subtitle">
          {{ ordersWord }} за эту неделю — подарок ваш.
        </div>

        <UiKitButton
          v-if="!isGiftInCart"
          color="success"
          size="sm"
          class="mt-1.5 self-start"
          data-testid="weekly-gift-add"
          @click="addGiftToCart"
        >
          Добавить подарок
        </UiKitButton>
        <div v-else class="weekly-gift__done" data-testid="weekly-gift-in-cart">
          Подарок уже в корзине
        </div>

        <div v-if="giftTierAlsoAvailable" class="weekly-gift__choice" data-testid="weekly-gift-choice">
          Подарок в заказе один — этот или за сумму заказа.
        </div>
      </template>

      <!-- Условие ещё не выполнено: показываем прогресс -->
      <template v-else>
        <div class="weekly-gift__title">
          Ещё <span class="weekly-gift__accent">{{ ordersLeft }}</span> {{ ordersLeftWord }} — и подарок
        </div>
        <div class="weekly-gift__subtitle">
          «{{ giftProduct?.name }}» за {{ progress.ordersRequired }} {{ requiredWord }} за неделю{{ amountHint }}
        </div>

        <div class="weekly-gift__bar">
          <div class="weekly-gift__bar-fill" :style="{ width: progressPercent + '%' }" />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import BasketGoalRing from './BasketGoalRing.vue';
import { BENEFIT_TONES } from './benefitTones';
import { useWeeklyGift } from '@features/basket';
import { pluralOrders } from '@shared/lib';

interface Props {
  /** Гостю одновременно доступен и подарок за сумму заказа — предупреждаем о выборе. */
  giftTierAlsoAvailable?: boolean;
}
defineProps<Props>();

// ⚠️ Прогресс грузит и перечитывает при смене филиала слайдер-родитель
// (BasketOrderInfoCards): карточка рождается только по результату загрузки,
// поэтому сама себя загрузить уже не может — она бы не смонтировалась.
const { progress, giftProduct, isGiftInCart, shouldShow, ordersLeft, addGiftToCart } =
  useWeeklyGift();

const ordersLeftWord = computed(() => pluralOrders(ordersLeft.value));
const requiredWord = computed(() => pluralOrders(progress.value.ordersRequired));
const ordersWord = computed(
  () => `${progress.value.ordersDone} ${pluralOrders(progress.value.ordersDone)}`,
);

const amountHint = computed(() =>
  progress.value.minOrderAmount > 0
    ? ` (каждый от ${progress.value.minOrderAmount.toLocaleString('ru-RU')} ₽)`
    : '',
);

const progressPercent = computed(() => {
  const required = progress.value.ordersRequired;
  if (required <= 0) return 0;
  return Math.min(100, Math.round((progress.value.ordersDone / required) * 100));
});
</script>

<style scoped lang="scss">
// Геометрия и типографика — как у соседних карточек слайдера (.info-card),
// чтобы лента выглядела одним набором, а не четырьмя разными блоками.
.weekly-gift {
  @apply relative flex items-center gap-3 w-full px-4 py-3 overflow-hidden;
  border-radius: var(--app-radius-card, 24px);
  @apply shadow-sm hover:shadow-md;
  @apply transition-all duration-300 ease-out;
  @apply hover:-translate-y-1 hover:shadow-lg;

  // Фон, рамка и акценты — из тона карточки (--benefit-tone, см. benefitTones.ts),
  // теми же формулами, что у соседей по ленте.
  background: linear-gradient(
    to bottom right,
    rgb(var(--benefit-tone) / 0.14),
    rgb(var(--benefit-tone) / 0.05)
  );
  border: 1px solid rgb(var(--benefit-tone) / 0.3);

  // Кольцо прогресса живёт в BasketGoalRing — подъём иконки достаём :deep.
  &:hover :deep(.goal-ring__badge) {
    @apply scale-110 rotate-3;
  }

  &__content {
    @apply flex flex-col flex-1 min-w-0 gap-1;
  }

  &__title {
    @apply font-bold text-sm tracking-tight leading-tight;
    @apply text-gray-900 dark:text-white tabular-nums;
    @apply md:text-xs;

    @media (max-width: 768px) {
      @apply text-xs;
    }
  }

  &__accent {
    color: rgb(var(--benefit-tone));
  }

  &__subtitle {
    @apply text-xs tracking-wide leading-tight;
    @apply text-gray-600 dark:text-gray-400;

    @media (max-width: 768px) {
      font-size: 0.65rem;
    }
  }

  &__done {
    @apply text-xs leading-tight text-green-600 dark:text-green-400;
  }

  &__choice {
    @apply text-gray-500 dark:text-gray-400 leading-tight;
    font-size: 0.65rem;
  }

  &__bar {
    @apply mt-0.5 h-1.5 w-full rounded-full overflow-hidden;
    background: rgb(var(--benefit-tone) / 0.18);
  }

  &__bar-fill {
    @apply h-full rounded-full;
    @apply transition-all duration-500 ease-out;
    background: rgb(var(--benefit-tone));
  }
}

// Сенсорные устройства: hover-подъём только мешает
@media (hover: none) and (pointer: coarse) {
  .weekly-gift:hover,
  .weekly-gift:hover :deep(.goal-ring__badge) {
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .weekly-gift,
  .weekly-gift__bar-fill {
    transition: none;
  }

  .weekly-gift:hover,
  .weekly-gift:hover :deep(.goal-ring__badge) {
    transform: none;
  }
}
</style>
